import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Box, Typography } from "@mui/material";
import { createLocalVideoTrack } from "livekit-client";
import {
  LocalUserChoices,
  usePersistentUserChoices,
} from "@livekit/components-react";
import PreJoinControlBar from "./PreJoinControlBar";
import { VideoOffIcon } from "lucide-react";

interface PreJoinScreenProps {
  containerized?: boolean;
  onJoin: (choices: LocalUserChoices) => void;
  showJoinButton?: boolean;
}

export interface PreJoinScreenRef {
  getCurrentChoices: () => LocalUserChoices;
}

const PreJoinScreen = forwardRef<PreJoinScreenRef, PreJoinScreenProps>(
  ({ containerized = false, onJoin, showJoinButton = true }, ref) => {
    const {
      userChoices,
      saveAudioInputEnabled,
      saveVideoInputEnabled,
      saveAudioInputDeviceId,
      saveVideoInputDeviceId,
    } = usePersistentUserChoices({
      defaults: {
        audioDeviceId: "",
        videoDeviceId: "",
        audioEnabled: true,
        videoEnabled: true,
        username: "User",
      },
    });

    const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
    const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
    const [localVideoTrack, setLocalVideoTrack] =
      useState<MediaStreamTrack | null>(null);

    // Use persistent user choices
    const audioEnabled = userChoices.audioEnabled;
    const videoEnabled = userChoices.videoEnabled;
    const selectedAudioDevice = userChoices.audioDeviceId;
    const selectedVideoDevice = userChoices.videoDeviceId;

    const handleVideoToggle = async () => {
      const newVideoEnabled = !videoEnabled;
      saveVideoInputEnabled(newVideoEnabled);

      if (newVideoEnabled) {
        const videoTrack = await createLocalVideoTrack(
          selectedVideoDevice ? { deviceId: selectedVideoDevice } : undefined,
        );
        setLocalVideoTrack(videoTrack.mediaStreamTrack);
      } else {
        if (localVideoTrack) {
          localVideoTrack.stop();
          setLocalVideoTrack(null);
        }
      }
    };

    const handleAudioToggle = () => {
      saveAudioInputEnabled(!audioEnabled);
    };

    const handleVideoDeviceChange = async (deviceId: string) => {
      saveVideoInputDeviceId(deviceId);

      if (videoEnabled) {
        if (localVideoTrack) {
          localVideoTrack.stop();
        }
        const videoTrack = await createLocalVideoTrack(
          deviceId ? { deviceId: deviceId } : undefined,
        );
        setLocalVideoTrack(videoTrack.mediaStreamTrack);
      }
    };

    const handleAudioDeviceChange = (deviceId: string) => {
      saveAudioInputDeviceId(deviceId);
    };

    const handleJoin = () => {
      const choices: LocalUserChoices = {
        username: "User",
        audioEnabled,
        videoEnabled,
        audioDeviceId: selectedAudioDevice,
        videoDeviceId: selectedVideoDevice,
      };

      if (localVideoTrack) {
        localVideoTrack.stop();
      }

      onJoin(choices);
    };

    useEffect(() => {
      const enumerateDevices = async () => {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(
          (device) => device.kind === "audioinput",
        );
        const videoInputs = devices.filter(
          (device) => device.kind === "videoinput",
        );

        setAudioDevices(audioInputs);
        setVideoDevices(videoInputs);

        // Use stored device if available, otherwise fallback to first device
        let finalAudioDevice = selectedAudioDevice;
        let finalVideoDevice = selectedVideoDevice;

        // Check if stored audio device is still available
        if (audioInputs.length > 0) {
          const isStoredAudioAvailable =
            selectedAudioDevice &&
            audioInputs.some(
              (device) => device.deviceId === selectedAudioDevice,
            );
          finalAudioDevice = isStoredAudioAvailable
            ? selectedAudioDevice
            : audioInputs[0].deviceId;

          if (finalAudioDevice !== selectedAudioDevice) {
            saveAudioInputDeviceId(finalAudioDevice);
          }
        }

        // Check if stored video device is still available
        if (videoInputs.length > 0) {
          const isStoredVideoAvailable =
            selectedVideoDevice &&
            videoInputs.some(
              (device) => device.deviceId === selectedVideoDevice,
            );
          finalVideoDevice = isStoredVideoAvailable
            ? selectedVideoDevice
            : videoInputs[0].deviceId;

          if (finalVideoDevice !== selectedVideoDevice) {
            saveVideoInputDeviceId(finalVideoDevice);
          }
        }

        // Create video track with the final selected device (and respect video enabled state)
        if (videoInputs.length > 0 && videoEnabled) {
          const videoTrack = await createLocalVideoTrack(
            finalVideoDevice ? { deviceId: finalVideoDevice } : undefined,
          );
          setLocalVideoTrack(videoTrack.mediaStreamTrack);
        }
      };

      enumerateDevices();
      return () => {
        if (localVideoTrack) {
          localVideoTrack.stop();
        }
      };
    }, []);

    useImperativeHandle(ref, () => ({
      getCurrentChoices: () => ({
        username: "User",
        audioEnabled,
        videoEnabled,
        audioDeviceId: selectedAudioDevice,
        videoDeviceId: selectedVideoDevice,
      }),
    }));

    return (
      <Box
        sx={{
          ...(containerized
            ? {
                width: "100%",
                height: "100%",
                borderRadius: "12px",
                overflow: "hidden",
              }
            : {
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1300,
              }),
          backgroundColor: "#232323",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px",
          gap: 3,
        }}
      >
        {/* Video Preview */}
        <Box
          sx={{
            backgroundColor: "#232323",
            borderRadius: "12px",
            overflow: "hidden",
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            aspectRatio: "16/9",
            flex: "1 1 auto",
            minHeight: 0,
          }}
        >
          {videoEnabled && localVideoTrack ? (
            <video
              ref={(videoElement) => {
                if (videoElement && localVideoTrack) {
                  videoElement.srcObject = new MediaStream([localVideoTrack]);
                }
              }}
              autoPlay
              muted
              playsInline
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "12px",
                transform: "scaleX(-1)",
              }}
            />
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
              }}
            >
              <VideoOffIcon size={64} />
              <Typography variant="body1" sx={{ mt: 1 }}>
                Camera is off
              </Typography>
            </Box>
          )}
        </Box>

        {/* Control Bar */}
        <PreJoinControlBar
          audioEnabled={audioEnabled}
          videoEnabled={videoEnabled}
          audioDevices={audioDevices}
          videoDevices={videoDevices}
          selectedAudioDevice={selectedAudioDevice}
          selectedVideoDevice={selectedVideoDevice}
          onAudioToggle={handleAudioToggle}
          onVideoToggle={handleVideoToggle}
          onAudioDeviceChange={handleAudioDeviceChange}
          onVideoDeviceChange={handleVideoDeviceChange}
          onJoin={handleJoin}
          showJoinButton={showJoinButton}
        />
      </Box>
    );
  },
);

export default PreJoinScreen;
